import { StringParam } from 'use-query-params';

import { DatasetTab } from 'components/StudyDashboard/useStudyDashboardTabState';
import {
  Annotation,
  AnnotationAssignment,
  AnnotationSummary,
  AnnotationWorkingStates,
  TodoDefinition,
} from 'interfaces/annotation';
import { filtersQueryParamsSchema } from 'utils/useEncodedFilters/constants';
import { encodeQueryParamsUsingSchema } from 'utils/useEncodedFilters/helpers';
import { analysisAreasExploreQuery, analysisAreasExploreQuerySchema } from './analysisAreasExplore';

export const ANALYSIS_AREAS_STEP_ID = 'analysis-areas';
export const ANALYSIS_AREAS_ASSIGNMENT_NAME = 'Analysis areas';
export const INCLUDE_AREA_CLASS = 'utils.include';
export const EXCLUDE_AREA_CLASS = 'utils.exclude';

export interface AnalysisAreaSlide {
  id: string;
  label: string;
}

export interface AnalysisAreaClassCounts {
  saved: number;
  draft: number;
}

export interface AnalysisAreaSlideCounts {
  slideId: string;
  include: AnalysisAreaClassCounts;
  exclude: AnalysisAreaClassCounts;
}

export const analysisAreaTodo = (): TodoDefinition => ({
  todo: 'Include / Exclude (Areas)',
  type: 'annotation_marker',
  options: [
    {
      name: EXCLUDE_AREA_CLASS,
      displayName: 'Exclude',
      color: '#ff0000',
      className: 'polygon',
    },
    {
      name: INCLUDE_AREA_CLASS,
      displayName: 'Include',
      color: '#00b300',
      className: 'polygon',
    },
  ],
});

const activeClassNames = (assignment: AnnotationAssignment): string[] =>
  (assignment.todos || []).flatMap((todo) =>
    (todo.options || []).filter((option) => !option.deletedAt).map((option) => option.name)
  );

export const isAnalysisAreaAssignment = (assignment: AnnotationAssignment): boolean => {
  const classNames = new Set(activeClassNames(assignment));
  return classNames.has(INCLUDE_AREA_CLASS) && classNames.has(EXCLUDE_AREA_CLASS);
};

const countSummaryValue = (value: AnnotationSummary[string] | undefined): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (!value || typeof value !== 'object') {
    return 0;
  }
  const positive = typeof value.positive === 'number' ? value.positive : 0;
  const negative = typeof value.negative === 'number' ? value.negative : 0;
  return positive + negative;
};

const classCounts = (annotation: Annotation | undefined, className: string): AnalysisAreaClassCounts => ({
  saved: countSummaryValue(annotation?.annotationSummary?.[className]),
  draft: countSummaryValue(annotation?.draftAnnotationSummary?.[className]),
});

export const getAnalysisAreaSlideCounts = (
  assignment: AnnotationAssignment,
  slideId: string
): AnalysisAreaSlideCounts => {
  const annotation = (assignment.annotations || []).find((item) => item.slideId === slideId);
  return {
    slideId,
    include: classCounts(annotation, INCLUDE_AREA_CLASS),
    exclude: classCounts(annotation, EXCLUDE_AREA_CLASS),
  };
};

export const hasDrawnAreas = (counts: AnalysisAreaClassCounts): boolean => counts.saved > 0 || counts.draft > 0;

export const getUndoneIncludeExcludeAnnotations = (assignment: AnnotationAssignment): Annotation[] =>
  (assignment.annotations || []).filter((annotation) => {
    if (annotation.workingState === AnnotationWorkingStates.Done) {
      return false;
    }
    const counts = getAnalysisAreaSlideCounts(assignment, annotation.slideId);
    return hasDrawnAreas(counts.include) || hasDrawnAreas(counts.exclude);
  });

const slideCountPhrase = (count: number, verb: string) => (count === 1 ? `1 slide ${verb}` : `${count} slides ${verb}`);

export const undoneIncludeExcludeWarning = (annotations: Annotation[]): string => {
  const annotating = annotations.filter(
    (annotation) => annotation.workingState === AnnotationWorkingStates.Annotating
  ).length;
  const qa = annotations.filter((annotation) => annotation.workingState === AnnotationWorkingStates.QA).length;
  const other = annotations.length - annotating - qa;
  const parts = [
    `${slideCountPhrase(
      annotations.length,
      annotations.length === 1 ? 'has' : 'have'
    )} Include or Exclude areas that are not marked Done.`,
  ];
  if (annotating) {
    parts.push(
      annotating === 1
        ? '1 slide is still being annotated, so its areas are left out of the run.'
        : `${annotating} slides are still being annotated, so their areas are left out of the run.`
    );
  }
  if (qa) {
    parts.push(
      `${slideCountPhrase(
        qa,
        qa === 1 ? 'is' : 'are'
      )} in QA. Those areas are still included, but they are not marked Done.`
    );
  }
  if (other) {
    parts.push('Mark the remaining slides Done before the run can use their areas.');
  }
  return parts.join(' ');
};

export const getAnalysisAreasDatasetUrl = ({
  studyId,
  labId,
  slideIds,
  annotationAssignmentId,
}: {
  studyId: string;
  labId?: string;
  slideIds: string[];
  annotationAssignmentId: number;
}) => {
  const search = encodeQueryParamsUsingSchema(
    {
      filters: { studyId },
      labId,
      slidesMode: true,
      datasetJobFilters: { studyId, slideIds },
      datasetJobFiltersIncludeExclude: 'include',
      studyDashboardTab: DatasetTab,
      ...analysisAreasExploreQuery(annotationAssignmentId),
    },
    {
      ...filtersQueryParamsSchema,
      ...analysisAreasExploreQuerySchema,
      studyDashboardTab: StringParam,
    }
  );

  return `/procedures?${search}`;
};
